// Ручка блока слева от абзаца/рисунка/таблицы (при наведении): «⠿» - перетащить блок мышью или открыть меню
// (вверх/вниз, дублировать, удалить, превратить в…), «+» - новый абзац ниже с «/»-меню вставки.
import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import type { MenuItemSpec } from '../../core/registry';
import { useApp } from '../../core/store';
import { ctx } from '../../core/ctx';
import { Icon } from '../../ui/icons';
import { MenuList, Popover } from '../../ui/controls';
import { moveBlock, duplicateBlock, deleteBlock } from './commands';

interface Hit { pos: number; top: number; height: number; type: string }

/** Блок верхнего уровня (в документе или приложении) под точкой экрана. */
function blockAt(editor: Editor, x: number, y: number): { pos: number; dom: HTMLElement; type: string } | null {
  const view = editor.view;
  const found = view.posAtCoords({ left: x, top: y });
  if (!found) return null;
  const $p = view.state.doc.resolve(found.inside >= 0 ? found.inside : found.pos);
  const isBlockParent = (name: string) => name === 'doc' || name === 'appendix';
  let pos = -1;
  if (isBlockParent($p.parent.type.name) && $p.nodeAfter && found.inside === $p.pos) pos = $p.pos;   // атом верхнего уровня
  else {
    for (let d = $p.depth; d >= 1; d--) {
      if (isBlockParent($p.node(d - 1).type.name)) { pos = $p.before(d); break; }
    }
  }
  if (pos < 0) return null;
  const node = view.state.doc.nodeAt(pos);
  if (!node || node.type.name === 'appendix' || node.type.name === 'appendixTitle') return null;
  const dom = view.nodeDOM(pos) as HTMLElement | null;
  if (!dom || dom.nodeType !== 1) return null;
  return { pos, dom, type: node.type.name };
}

export function BlockHandle({ sheet }: { sheet: React.RefObject<HTMLDivElement | null> }) {
  const editor = useApp((s) => s.editor);
  const [hit, setHit] = useState<Hit | null>(null);
  const [menu, setMenu] = useState(false);
  const gripRef = useRef<HTMLButtonElement>(null);
  const hitRef = useRef<Hit | null>(null);
  hitRef.current = hit;

  useEffect(() => {
    const el = sheet.current;
    if (!el || !editor) return;
    let raf = 0;
    const move = (e: MouseEvent) => {
      if ((e.target as HTMLElement)?.closest?.('.block-handle')) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (editor.isDestroyed || menu) return;
        const sr = el.getBoundingClientRect();
        const scale = sr.width / el.offsetWidth || 1;
        // точка внутри текста той же строки (если мышь на левом поле)
        const inner = editor.view.dom.getBoundingClientRect();
        const x = Math.max(e.clientX, inner.left + 8);
        const b = blockAt(editor, Math.min(x, inner.right - 8), e.clientY);
        if (!b) { setHit(null); return; }
        const r = b.dom.getBoundingClientRect();
        setHit({ pos: b.pos, top: (r.top - sr.top) / scale, height: r.height / scale, type: b.type });
      });
    };
    const leave = (e: MouseEvent) => {
      const to = e.relatedTarget as HTMLElement | null;
      if (to?.closest?.('.block-handle')) return;
      if (!menu) setHit(null);
    };
    const key = () => { if (!menu) setHit(null); };
    el.addEventListener('mousemove', move);
    el.addEventListener('mouseleave', leave);
    el.addEventListener('keydown', key);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('mousemove', move);
      el.removeEventListener('mouseleave', leave);
      el.removeEventListener('keydown', key);
    };
  }, [editor, sheet, menu]);

  if (!editor || !hit) return null;

  const onDragStart = (e: React.DragEvent) => {
    const h = hitRef.current;
    if (!h || editor.isDestroyed) return;
    const view = editor.view;
    const sel = NodeSelection.create(view.state.doc, h.pos);
    view.dispatch(view.state.tr.setSelection(sel));
    const slice = sel.content();
    const { dom, text } = (view as any).serializeForClipboard(slice);
    e.dataTransfer.clearData();
    e.dataTransfer.setData('text/html', dom.innerHTML);
    e.dataTransfer.setData('text/plain', text);
    e.dataTransfer.effectAllowed = 'copyMove';
    const nd = view.nodeDOM(h.pos) as HTMLElement | null;
    if (nd) e.dataTransfer.setDragImage(nd, 0, 0);
    (view as any).dragging = { slice, move: true };
  };

  const addBelow = () => {
    const h = hitRef.current;
    if (!h) return;
    const node = editor.state.doc.nodeAt(h.pos);
    if (!node) return;
    const end = h.pos + node.nodeSize;
    const tr = editor.state.tr.insert(end, editor.schema.nodes.paragraph.create(null, editor.schema.text('/')));
    tr.setSelection(TextSelection.create(tr.doc, end + 2));
    editor.view.dispatch(tr.scrollIntoView());
    editor.view.focus();
  };

  const items: MenuItemSpec[] = [
    { label: 'Переместить вверх', icon: 'up', shortcut: 'Alt-Shift-ArrowUp', onClick: () => moveBlock(editor, -1, hit.pos) },
    { label: 'Переместить вниз', icon: 'arrow-down', shortcut: 'Alt-Shift-ArrowDown', onClick: () => moveBlock(editor, 1, hit.pos) },
    { label: 'Дублировать', icon: 'duplicate', shortcut: 'Mod-Shift-d', onClick: () => duplicateBlock(editor, hit.pos) },
    { label: 'Выделить блок', icon: 'cursor', onClick: () => { editor.view.dispatch(editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, hit.pos))); editor.view.focus(); } },
  ];
  if (['paragraph', 'heading', 'stoList'].includes(hit.type)) {
    const set = (what: string) => () => {
      editor.view.dispatch(editor.state.tr.setSelection(TextSelection.near(editor.state.doc.resolve(hit.pos))));
      ctx.run('block.turnInto', what);
    };
    items.push({ separator: true }, { label: 'Превратить в', icon: 'paragraph', children: [
      { label: 'Текст', icon: 'paragraph', onClick: set('paragraph') },
      { label: 'Раздел', icon: 'h1', onClick: set('h1') },
      { label: 'Подраздел', icon: 'h2', onClick: set('h2') },
      { label: 'Пункт', icon: 'h3', onClick: set('h3') },
      { label: 'Перечисление «–»', icon: 'list', onClick: set('dash') },
      { label: 'Перечисление «а)»', icon: 'list-tree', onClick: set('letter') },
      { label: 'Перечисление «1)»', icon: 'list-digit', onClick: set('digit') },
    ] });
  }
  items.push({ separator: true }, { label: 'Удалить', icon: 'trash', shortcut: 'Mod-Shift-Backspace', onClick: () => deleteBlock(editor, hit.pos) });

  const line = hit.type === 'figure' || hit.type === 'stoTable' || hit.type === 'formula' ? Math.min(hit.height / 2 - 11, 40) : 4;
  return (
    <div className="block-handle" style={{ top: hit.top + Math.max(0, line) }} data-testid="block-handle"
      onMouseLeave={(e) => { if (!menu && !(e.relatedTarget as HTMLElement)?.closest?.('.sheet-main')) setHit(null); }}>
      <button type="button" className="bh-btn" data-tip="Новый абзац ниже" data-tip-desc="С меню вставки «/»" onMouseDown={(e) => e.preventDefault()}
        onClick={addBelow} aria-label="Добавить блок ниже"><Icon name="plus" size={15} /></button>
      <button type="button" ref={gripRef} className="bh-btn bh-grip" draggable data-testid="block-grip"
        data-tip="Перетащите, чтобы переместить" data-tip-desc="Щелчок - меню блока" aria-label="Меню блока"
        onMouseDown={(e) => e.stopPropagation()} onDragStart={onDragStart} onDragEnd={() => setHit(null)}
        onClick={() => setMenu(true)}><Icon name="grip" size={15} /></button>
      {menu && (
        <Popover anchor={gripRef.current} onClose={() => { setMenu(false); setHit(null); }}>
          <MenuList items={items} onDone={() => { setMenu(false); setHit(null); }} autoFocus />
        </Popover>
      )}
    </div>
  );
}
